<template>
  <div v-if="showDialog" class="dialog-overlay" @click="closeDialog">
    <div class="dialog" @click.stop>
      <header class="dialog-header">
        <h3>Create Content</h3>
        <button class="close-button" @click="closeDialog">
          <i class="fas fa-times"></i>
        </button>
      </header>
      <div class="dialog-body">
        <!-- title -->
        <div class="card form-group" :class="{ 'has-error': errors.title }">
          <label for="title" class="form-label">Title:</label>
          <input
            id="title"
            v-model="title"
            type="text"
            class="form-control"
            placeholder="Enter title"
            @input="clearError('title')"
            :disabled="loading"
          />
          <span v-if="errors.title" class="error-text">{{ errors.title }}</span>
        </div>
        <!-- Description -->
        <div
          class="card form-group"
          :class="{ 'has-error': errors.description }"
        >
          <label for="description" class="form-label">Description:</label>
          <input
            id="description"
            v-model="description"
            type="text"
            class="form-control"
            placeholder="Enter description"
            @input="clearError('description')"
            :disabled="loading"
          />
          <span v-if="errors.description" class="error-text">{{
            errors.description
          }}</span>
        </div>
        <!-- fileTitle -->
        <div class="card form-group" :class="{ 'has-error': errors.fileTitle }">
          <label for="fileTitle" class="form-label">File TItle:</label>
          <input
            id="fileTitle"
            v-model="fileTitle"
            type="text"
            class="form-control"
            placeholder="Enter fileTitle"
            @input="clearError('fileTitle')"
            :disabled="loading"
          />
          <span v-if="errors.fileTitle" class="error-text">{{
            errors.fileTitle
          }}</span>
        </div>
        <!-- fileLink -->
        <div class="card form-group" :class="{ 'has-error': errors.fileLink }">
          <label for="fileLink" class="form-label">File Link:</label>
          <input
            id="fileLink"
            v-model="fileLink"
            type="text"
            class="form-control"
            placeholder="Enter fileLink"
            @input="clearError('fileLink')"
            :disabled="loading"
          />
          <span v-if="errors.fileLink" class="error-text">{{
            errors.fileLink
          }}</span>
        </div>
        <!-- fileButtonText -->
        <div
          class="card form-group"
          :class="{ 'has-error': errors.fileButtonText }"
        >
          <label for="fileButtonText" class="form-label"
            >File Button Text:</label
          >
          <input
            id="fileButtonText"
            v-model="fileButtonText"
            type="text"
            class="form-control"
            placeholder="Enter fileButtonText"
            @input="clearError('fileButtonText')"
            :disabled="loading"
          />
          <span v-if="errors.fileButtonText" class="error-text">{{
            errors.fileButtonText
          }}</span>
        </div>
        <!-- type -->
        <div class="card form-group" :class="{ 'has-error': errors.type }">
          <label for="type" class="form-label">Type:</label>
          <select
            id="type"
            v-model="type"
            class="form-control"
            @change="clearError('type')"
            :disabled="loading"
          >
            <option value="banner">Banner</option>
            <option value="video">Video</option>
          </select>
          <span v-if="errors.type" class="error-text">{{ errors.type }}</span>
        </div>
        <!-- Banner -->
        <div
          v-if="type === 'banner'"
          class="card form-group"
          :class="{ 'has-error': errors.banner }"
        >
          <label for="banner" class="form-label">Banner:</label>
          <input
            id="banner"
            type="file"
            @change="handleBannerUpload"
            class="form-control"
            :disabled="loading"
          />
          <span v-if="errors.banner" class="error-text">{{
            errors.banner
          }}</span>
        </div>
        <!-- videoLink -->
        <div
          v-if="type === 'video'"
          class="card form-group"
          :class="{ 'has-error': errors.videoLink }"
        >
          <label for="videoLink" class="form-label">Video Link:</label>
          <input
            id="videoLink"
            v-model="videoLink"
            type="text"
            class="form-control"
            placeholder="Enter video URL"
            @input="clearError('videoLink')"
            :disabled="loading"
          />
          <span v-if="errors.videoLink" class="error-text">{{
            errors.videoLink
          }}</span>
        </div>
        <!-- Content -->
        <div class="card form-group" :class="{ 'has-error': errors.content }">
          <label for="content" class="form-label">Content:</label>

          <ckEditor
            :disabled="loading"
            id="content"
            :contentProp="content"
            @updateContent="updateContentValue"
          />
          <span v-if="errors.content" class="error-text">{{
            errors.content
          }}</span>
        </div>
        <!-- Action button -->
        <button
          class="create-button"
          @click="createContent"
          :disabled="loading"
        >
          <span v-if="loading" class="spinner"></span>
          <span v-else>Create</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, onMounted, watch } from "vue";

// Api
import axios from "axios";
import { getBaseUrl } from "@/helper/constant.js";

import ckEditor from "@/components/ckEditor.vue";

const props = defineProps(["openDialog"]);
const emit = defineEmits(["close", "submit"]);

const showDialog = ref(false);
const title = ref("");
const description = ref("");
const type = ref("banner");
const banner = ref("");
const videoLink = ref("");
const fileLink = ref("");
const fileTitle = ref("");
const fileButtonText = ref("");
const content = ref("<p>Hello There.</p>");
const errors = ref({});
const loading = ref(false);

const updateContentValue = (newContent) => {
  content.value = newContent;
};
const openDialog = () => {
  showDialog.value = true;
  clearAllValues();
};
const closeDialog = () => {
  showDialog.value = false;
  clearAllValues();
  emit("close");
};
const submitDialog = () => {
  showDialog.value = false;
  clearAllValues();
  emit("submit");
};
const handleBannerUpload = (event) => {
  const file = event.target.files[0];
  const validExtensions = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
  if (!validExtensions.includes(file.type)) {
    errors.value.banner = "Only image files (jpg, jpeg, png, gif) are allowed.";
    banner.value = "";
    return;
  }
  const reader = new FileReader();
  reader.onload = (e) => {
    banner.value = e.target.result;
  };
  reader.readAsDataURL(file);
};
const validateInputs = () => {
  errors.value = {};
  if (!type.value?.trim()) errors.value.title = "Type is required.";
  if (!title.value?.trim()) errors.value.title = "Title is required.";
  if (!description.value?.trim())
    errors.value.description = "Description is required.";
  if (type.value === "banner" && !banner.value)
    errors.value.banner = "Banner image is required.";
  if (type.value === "video" && !/^https?:\/\/.+/.test(videoLink.value))
    errors.value.videoLink = "Valid video URL is required.";
  if (!content.value?.trim()) errors.value.content = "Content is required.";
  return Object.keys(errors.value).length === 0;
};
const clearError = (field) => {
  errors.value[field] = "";
};
const clearAllValues = () => {
  title.value = "";
  description.value = "";
  type.value = "banner";
  banner.value = "";
  videoLink.value = "";
  fileLink.value = "";
  fileTitle.value = "";
  fileButtonText.value = "";
  content.value = "";
  errors.value = {};
};
async function createContent() {
  if (loading.value || !validateInputs()) return;
  loading.value = true;
  try {
    const response = await axios.post(getBaseUrl() + `/core/content`, {
      title: title.value,
      description: description.value,
      banner: banner.value,
      type: type.value,
      videoLink: videoLink.value,
      fileLink: fileLink.value,
      fileTitle: fileTitle.value,
      fileButtonText: fileButtonText.value,
      content: content.value,
    });
    window.alert("Content Created Successfully");
  } catch (err) {
    console.log(err);
    window.alert(
      "There was an error creating the content. Please Content a developer",
    );
  } finally {
    loading.value = false;
    submitDialog();
  }
}
onMounted(() => {
  if (props.openDialog) {
    openDialog();
  }
});
watch(
  () => props.openDialog,
  (newVal) => {
    if (newVal) {
      openDialog();
    } else {
      closeDialog();
    }
  },
);
</script>

<style scoped>
@import url("https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css");

.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 50;
}

.dialog {
  background: white;
  padding: 20px;
  border-radius: 0; /* Remove border-radius for full screen */
  width: 100%;
  height: 100%; /* Make dialog full height */
  max-width: 100%; /* Remove max-width constraint */
  max-height: 100%; /* Remove max-height constraint */
  box-shadow: none; /* Remove box-shadow */
  position: relative;
  animation: slide-down 0.3s ease-out;
  display: flex;
  flex-direction: column; /* Ensure content is vertically aligned */
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ddd;
  padding-bottom: 10px;
  margin-bottom: 20px;
}

.dialog-body {
  flex: 1; /* Allow body to take up remaining space */
  overflow-y: auto;
}

.close-button {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
}

.close-button i {
  font-size: 20px;
  color: #333;
}

.card {
  background: #f9f9f9;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.form-label {
  font-weight: bold;
  margin-bottom: 5px;
  display: block;
}

.form-control {
  width: 100%;
  padding: 8px;
  box-sizing: border-box;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.form-group.has-error .form-control {
  border-color: red;
}

.error-text {
  color: red;
  font-size: 12px;
  margin-top: 5px;
}

.create-button {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.create-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.spinner {
  border: 2px solid #f3f3f3;
  border-top: 2px solid #007bff;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
}

@keyframes slide-down {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
